import { PermissionEnum } from '@api/enum';
import { IUser } from '@api/models';
import { IDocumentFileModel, IDocumentFolderModel, IDocumentFolderWithAccess } from '@api/models/document.model';
import DocFileIcon from '@components/DocFileIcon/DocFileIcon';
import EventBlocker from '@components/EventBlocker/EventBlocker';
import OverflowTooltip from '@components/OverflowTooltip/OverflowTooltip';
import useAuthPermission from '@hooks/use-auth-permission';
import useAuthSelector from '@hooks/use-auth-selector';
import { Button, Card, Table } from '@hyperflake/react-ui-library';
import { EllipsisVerticalIcon, FolderIcon, FolderPlusIcon, PlusIcon, UploadIcon } from '@icon/index';
import { Avatar, AvatarGroup } from '@library/avatar';
import { Dropdown, DropdownItem, DropdownList, DropdownTrigger } from '@library/dropdown';
import DropdownPortal from '@library/dropdown/components/DropdownPortal';
import { TRootState } from '@store/index';
import clsx from 'clsx';
import { saveAs } from 'file-saver';
import FolderBreadcrumbs from 'modules/box-strorage/feature/folder-breadcrumb/FolderBreadcrumb';
import TzDateFormatter from 'modules/shared/helpers/TzDateFormatter';

import { convertFileSize, getFileExtension } from 'modules/shared/utils/file-utils';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { FC, ReactNode, HTMLAttributes, TdHTMLAttributes } from 'react';

type TRowProps = {
    children: ReactNode;
} & HTMLAttributes<HTMLTableRowElement>;

export const TRow = ({ children, className, onClick, ...rest }: TRowProps) => {
    const clickable = typeof onClick === 'function';

    return (
        <tr
            {...rest}
            onClick={onClick}
            className={clsx('border-b border-grayscale-100', clickable && 'cursor-pointer', className)}
        >
            {children}
        </tr>
    );
};

type TCellProps = {
    children: ReactNode;
    maxWidth?: string; // e.g. "14vw", "240px"
    align?: 'left' | 'center' | 'right';
    className?: string;
} & TdHTMLAttributes<HTMLTableCellElement>;

export const TCell = ({ children, maxWidth, align = 'left', className, style, ...rest }: TCellProps) => {
    return (
        <td
            {...rest}
            className={clsx('px-3 py-3 align-middle', className)}
            style={{
                ...style,
                maxWidth,
                ...(maxWidth
                    ? {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                      }
                    : {}),
                textAlign: align,
            }}
        >
            {children}
        </td>
    );
};

interface DocumentListProps {
    breadcrumb: { name: string; folderId: string }[];
    folders: IDocumentFolderWithAccess[];
    files: IDocumentFileModel[];
    onFolderClick: (folderId: string) => void;
    onFolderEdit?: (folder: IDocumentFolderModel) => void;
    onFolderDelete?: (folder: IDocumentFolderModel) => void;
    onMoveFolderClick?: (folder: IDocumentFolderModel) => void;
    onUserAccessClick: (file: IDocumentFileModel) => void;
    onDeleteClick: (file: IDocumentFileModel) => void;
    onUploadFileClick: () => void;
    onMoveFileClick: (file: IDocumentFileModel) => void;
    onRenameFileClick: (file: IDocumentFileModel) => void;
    onAddFolderClick: () => void;
    onUserFolderAccessClick: (file: IDocumentFolderWithAccess) => void;
}

type TableItem =
    | { _id: string; type: 'folder'; data: IDocumentFolderWithAccess }
    | { _id: string; type: 'file'; data: IDocumentFileModel };

const DocumentList = (props: DocumentListProps) => {
    const {
        breadcrumb,
        folders,
        files,
        onFolderClick,
        onFolderEdit,
        onFolderDelete,
        onMoveFolderClick,
        onUserAccessClick,
        onDeleteClick,
        onUploadFileClick,
        onMoveFileClick,
        onRenameFileClick,
        onUserFolderAccessClick,
        onAddFolderClick,
    } = props;

    const [searchParams, setSearchParams] = useSearchParams();

    const { hasPermission } = useAuthPermission();

    // const { user: authUser } = useAuthSelector();
    const { info: authUser } = useSelector((state: TRootState) => state.profile);

    const [sortBy, setSortBy] = useState<string>('');
    const [sortOrder, setSortOrder] = useState<number>(1);

    const selectedDocumentIdRef = useRef<string | null>(null);

    const selectedDocumentId = searchParams.get('id');

    const tableItems = useMemo(() => {
        const folderItems: TableItem[] = folders.map((folder) => ({
            _id: folder._id,
            type: 'folder' as const,
            data: folder,
        }));

        const fileItems: TableItem[] = files.map((file) => ({
            _id: file._id,
            type: 'file' as const,
            data: file,
        }));

        return [...folderItems, ...fileItems];
    }, [folders, files]);

    const sortedItems = useMemo(() => {
        if (!sortBy) return tableItems;

        const getComparable = (item: TableItem) => {
            const v: any = (item.data as any)[sortBy];

            if (v == null) return null;

            // common cases
            if (sortBy === 'createdAt') return new Date(v).getTime();
            if (sortBy === 'fileSize' || sortBy === 'folderSize') return Number(v);

            // default string compare
            return String(v).toLowerCase();
        };

        return [...tableItems].sort((a, b) => {
            // Always put folders first
            if (a.type === 'folder' && b.type === 'file') return -1;
            if (a.type === 'file' && b.type === 'folder') return 1;

            const av = getComparable(a);
            const bv = getComparable(b);

            if (av == null && bv == null) return 0;
            if (av == null) return 1;
            if (bv == null) return -1;

            const dir = sortOrder === 1 ? 1 : -1;

            // number compare
            if (typeof av === 'number' && typeof bv === 'number') {
                return (av - bv) * dir;
            }

            // string compare
            return String(av).localeCompare(String(bv)) * dir;
        });
    }, [sortBy, sortOrder, tableItems]);

    useEffect(() => {
        selectedDocumentIdRef.current = selectedDocumentId;
    }, [selectedDocumentId]);

    useEffect(() => {
        if (selectedDocumentIdRef.current) {
            const element = document.getElementById(`document-link-row-${selectedDocumentIdRef.current}`);

            if (element) {
                setTimeout(() => {
                    element.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                    });
                }, 100);
            }

            const timer = setTimeout(() => {
                const newParams = new URLSearchParams(searchParams);
                newParams.delete('id');

                setSearchParams(newParams, { replace: true });
            }, 8000);
            return () => clearTimeout(timer);
        }
    }, [sortedItems, searchParams, setSearchParams]);

    const handleSortChange = (sortBy: string, sortOrder: number) => {
        setSortBy(sortBy);
        setSortOrder(sortOrder);
    };

    function handleFileDownload(file: IDocumentFileModel) {
        try {
            saveAs(file.url, file.name);
        } catch (ex: any) {
            toast.error(ex?.data?.message || 'Some error occurred, please try again.');

            console.log(ex);
        }
    }

    const renderUploadedBy = (user: IUser) => {
        if (user._id === authUser._id)
            return (
                <div key={user._id} className="flex items-center gap-3">
                    <div>
                        <Avatar
                            src={user.dpUrl}
                            alt={user.name}
                            // backgroundColor={user?.preset?.color}
                        />
                    </div>

                    <div>You</div>
                </div>
            );

        return (
            <div className="flex items-center gap-3">
                <div>
                    <Avatar
                        src={user.dpUrl}
                        alt={user.name}
                        //  backgroundColor={user?.preset?.color}
                    />
                </div>

                <div className="overflow-hidden">
                    <OverflowTooltip>{user.name}</OverflowTooltip>
                    <div className="text-xs font-normal text-grayscale-500">
                        {user.designation ? (
                            <OverflowTooltip>{user.designation}</OverflowTooltip>
                        ) : (
                            <i>No designation</i>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderFolderRow = (folder: IDocumentFolderWithAccess) => {
        const createdBy = folder.createdBy as IUser;
        const canEdit = hasPermission(PermissionEnum.DOCUMENT_UPDATE) || createdBy._id === authUser._id;
        const canDelete = hasPermission(PermissionEnum.DOCUMENT_DELETE) || createdBy._id === authUser._id;

        return (
            <TRow
                key={`folder-${folder._id}`}
                className="bg-card-color hover:bg-grayscale-50 cursor-pointer"
                onClick={() => onFolderClick(folder._id)}
            >
                <TCell maxWidth="14vw">
                    <div className="flex items-center gap-3">
                        <div>
                            {/* <DocFileIcon className="w-7" extension={getFileExtension('folder')} /> */}
                            {/* <img className="w-7" src="/icons/folder.svg" alt="" /> */}
                            <FolderIcon />
                        </div>
                        <OverflowTooltip>{folder.name}</OverflowTooltip>
                    </div>
                </TCell>
                <TCell>
                    <TzDateFormatter date={folder.createdAt} format={'DD MMM, YYYY'} />
                </TCell>
                <TCell>{convertFileSize(+folder.folderSize)}</TCell>
                <TCell maxWidth="15vw">{renderUploadedBy(createdBy)}</TCell>
                <TCell
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                    }}
                >
                    <div
                        className="cursor-pointer"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onUserFolderAccessClick(folder);
                        }}
                    >
                        {folder.users.length > 0 ? (
                            <AvatarGroup
                                avatars={(folder.users as IUser[]).map((row) => ({
                                    src: row.dpUrl,
                                    alt: row.name,
                                    backgroundColor: row?.preset?.color,
                                }))}
                            />
                        ) : (
                            'None'
                        )}
                    </div>
                </TCell>

                <TCell>
                    <EventBlocker stopPropagation>
                        <Dropdown>
                            <DropdownTrigger className="btn-action">
                                <EllipsisVerticalIcon className="text-grayscale-500" />
                            </DropdownTrigger>

                            <DropdownPortal>
                                <DropdownList>
                                    <DropdownItem onClick={() => onFolderEdit(folder)}>Rename</DropdownItem>
                                    <DropdownItem onClick={() => onMoveFolderClick(folder)}>Move</DropdownItem>

                                    <DropdownItem
                                        //  disabled={!canDelete}
                                        onClick={() => onFolderDelete(folder)}
                                    >
                                        <span className="text-danger-500">Delete</span>
                                    </DropdownItem>
                                </DropdownList>
                            </DropdownPortal>
                        </Dropdown>
                    </EventBlocker>
                </TCell>
            </TRow>
        );
    };

    const renderFileRow = (file: IDocumentFileModel) => {
        const createdBy = file.uploadedBy as IUser;
        const canDelete =
            hasPermission(PermissionEnum.DOCUMENT_DELETE) || (file.uploadedBy as IUser)._id === authUser._id;
        return (
            <TRow
                key={`file-${file._id}`}
                id={`document-link-row-${file._id}`}
                className={clsx('bg-card-color', selectedDocumentId === file._id && 'animate-bg-fade')}
            >
                <TCell maxWidth="14vw">
                    <div className="flex items-center gap-3">
                        <div>
                            <DocFileIcon className="w-7" extension={getFileExtension(file.path)} />
                        </div>

                        <OverflowTooltip>{file.name}</OverflowTooltip>
                    </div>
                </TCell>
                <TCell>
                    <TzDateFormatter date={file.createdAt} format={'DD MMM, YYYY'} />
                </TCell>
                <TCell>{convertFileSize(+file.fileSize)}</TCell>
                <TCell maxWidth="15vw">{renderUploadedBy(createdBy)}</TCell>
                <TCell>
                    <div className="cursor-pointer" onClick={() => onUserAccessClick(file)}>
                        {file.users.length > 0 ? (
                            <AvatarGroup
                                avatars={(file.users as IUser[]).map((row) => ({
                                    src: row.dpUrl,
                                    alt: row.name,
                                    backgroundColor: row?.preset?.color,
                                }))}
                            />
                        ) : (
                            'None'
                        )}
                    </div>
                </TCell>
                <TCell>
                    <Dropdown>
                        <DropdownTrigger className="btn-action">
                            <EllipsisVerticalIcon className="text-grayscale-500" />
                        </DropdownTrigger>

                        <DropdownPortal>
                            <DropdownList>
                                <DropdownItem onClick={() => onRenameFileClick(file)}>Rename</DropdownItem>
                                <DropdownItem onClick={() => onMoveFileClick(file)}>Move</DropdownItem>
                                <DropdownItem onClick={() => handleFileDownload(file)}>Download</DropdownItem>
                                {/* <DropdownItem onClick={() => handleFileDownload(file)}>Move</DropdownItem> */}

                                <DropdownItem
                                    //  disabled={!canDelete}
                                    onClick={() => onDeleteClick(file)}
                                >
                                    <span className="text-danger-500">Delete</span>
                                </DropdownItem>
                            </DropdownList>
                        </DropdownPortal>
                    </Dropdown>
                </TCell>
            </TRow>
        );
    };

    return (
        <>
            <Card variant="wrapper" className="overflow-hidden">
                <div className="p-4 pb-0 overflow-x-auto">
                    <div className="flex items-center gap-4 ">
                        <FolderBreadcrumbs
                            items={[{ name: 'All Documents', folderId: null }, ...breadcrumb]}
                            onItemClick={onFolderClick}
                        />

                        <div className="flex justify-end items-center gap-4 ml-auto">
                            <Button color="secondary" size="xs" onClick={onAddFolderClick}>
                                <PlusIcon width="10" height="10" /> Add Folder
                            </Button>

                            <Button size="xs" onClick={onUploadFileClick}>
                                <UploadIcon width="12" height="14" /> Upload File
                            </Button>
                        </div>
                    </div>

                    <Table
                        className="mt-2"
                        borderStyle="divider"
                        hover
                        // isLoading={isLoading} // if you have it
                        data={sortedItems}
                        columns={[
                            { header: 'Name' },
                            { header: 'Date' },
                            { header: 'File Size' },
                            { header: 'Created By' },
                            { header: 'Access' },
                            { header: 'Action' },
                        ]}
                        renderRow={(item) => {
                            if (item.type === 'folder') {
                                return renderFolderRow(item.data);
                            } else {
                                return renderFileRow(item.data);
                            }
                        }}
                    />
                </div>
            </Card>
        </>
    );
};

export default DocumentList;
